import { getAuthToken } from "@/lib/storage"
import { getSocketBaseUrl } from "@/lib/socketBase"
import { io, Socket } from "socket.io-client"
import {
  CityChatBan,
  CityChatBanDuration,
  CityChatMessage,
  CityChatState,
} from "../types/cityChat"

const API_BASE = getSocketBaseUrl()

type CityChatEmitEvents = {
  "chat-refresh": (payload?: { cityId?: number }) => void
  "chat-join": (payload: { cityId: number }) => void
  "chat-send": (payload: { text: string; forGuild?: boolean }) => void
  "chat-ban": (payload: {
    userId: number
    duration: CityChatBanDuration
    reason?: string
  }) => void
  "chat-unban": (payload: { userId: number }) => void
  "chat-delete": (payload: { messageId: string }) => void
}

type CityChatListenEvents = {
  "chat-state": (payload: CityChatState) => void
  "chat-message": (payload: CityChatMessage) => void
  "chat-message-deleted": (payload: { cityId: number; messageId: string }) => void
  "chat-cleared": (payload: { cityId: number; at?: string }) => void
  "chat-banned": (payload: CityChatBan) => void
  "chat-unbanned": (payload: { cityId: number; userId: number }) => void
  "chat-ban-ack": (payload: { ok: boolean; ban?: CityChatBan }) => void
  "chat-unban-ack": (payload: { ok: boolean; userId: number }) => void
  "chat-delete-ack": (payload: { ok: boolean; messageId: string }) => void
  "chat-join-ack": (payload: { ok: boolean; cityId: number }) => void
  connect_error: (payload: { message?: string }) => void
  error: (payload: { message?: string }) => void
}

type RegisteredListener = {
  event: keyof CityChatListenEvents
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: (...args: any[]) => void
}

let socket: Socket | null = null
let lastAuthToken: string | null = null
let connectPromise: Promise<Socket> | null = null
let connectPromiseToken: string | null = null

const registeredListeners: RegisteredListener[] = []

function normalizeAuthToken(raw: string | null | undefined): string {
  return raw?.replace(/^Bearer\s+/i, "").trim() ?? ""
}

function bindListener(sock: Socket, entry: RegisteredListener) {
  const s = sock as Socket & {
    on: (ev: string, fn: (...args: unknown[]) => void) => void
    off: (ev: string, fn: (...args: unknown[]) => void) => void
  }
  s.off(entry.event, entry.handler as (...args: unknown[]) => void)
  s.on(entry.event, entry.handler as (...args: unknown[]) => void)
}

function bindAllRegisteredListeners(sock: Socket) {
  for (const entry of registeredListeners) {
    bindListener(sock, entry)
  }
}

function createCityChatSocket(token: string): Socket {
  const sock = io(`${API_BASE}/game/city-chat`, {
    auth: { token },
    transports: ["websocket", "polling"],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  })
  bindAllRegisteredListeners(sock)
  return sock
}

function clearConnectPromise(token: string) {
  if (connectPromiseToken === token) {
    connectPromise = null
    connectPromiseToken = null
  }
}

function teardownCityChatSocket() {
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
}

function waitForSocketConnect(sock: Socket): Promise<Socket> {
  if (sock.connected) return Promise.resolve(sock)

  return new Promise((resolve, reject) => {
    const onConnect = () => {
      cleanup()
      resolve(sock)
    }
    const onError = (err: Error) => {
      cleanup()
      reject(err)
    }
    const cleanup = () => {
      sock.off("connect", onConnect)
      sock.off("connect_error", onError)
    }

    sock.once("connect", onConnect)
    sock.once("connect_error", onError)
    if (!sock.connected) {
      sock.connect()
    }
  })
}

async function connectCityChatSocketInternal(token: string): Promise<Socket> {
  if (socket?.connected && lastAuthToken === token) {
    return socket
  }

  if (socket && lastAuthToken === token && !socket.connected) {
    return waitForSocketConnect(socket)
  }

  if (socket) {
    teardownCityChatSocket()
  }

  lastAuthToken = token
  socket = createCityChatSocket(token)
  return waitForSocketConnect(socket)
}

export async function connectCityChatSocket(
  explicitToken?: string | null
): Promise<Socket> {
  const stored = explicitToken ?? getAuthToken()
  const token = normalizeAuthToken(stored)

  if (!token) {
    throw new Error("JWT required for /game/city-chat")
  }

  if (socket?.connected && lastAuthToken === token) {
    return socket
  }

  if (connectPromise && connectPromiseToken === token) {
    return connectPromise
  }

  connectPromiseToken = token
  connectPromise = connectCityChatSocketInternal(token)
    .catch((err) => {
      clearConnectPromise(token)
      throw err
    })
    .finally(() => {
      clearConnectPromise(token)
    })

  return connectPromise
}

export function disconnectCityChatSocket() {
  connectPromise = null
  connectPromiseToken = null
  teardownCityChatSocket()
  lastAuthToken = null
}

export function getCityChatSocket(): Socket | null {
  return socket
}

export function emitCityChatEvent<K extends keyof CityChatEmitEvents>(
  event: K,
  ...args: Parameters<CityChatEmitEvents[K]>
) {
  if (!socket?.connected) return false
  socket.emit(event, ...args)
  return true
}

export function onCityChatEvent<K extends keyof CityChatListenEvents>(
  event: K,
  handler: CityChatListenEvents[K]
) {
  const entry: RegisteredListener = { event, handler }
  registeredListeners.push(entry)
  if (socket) {
    bindListener(socket, entry)
  }
  return () => {
    const idx = registeredListeners.indexOf(entry)
    if (idx >= 0) registeredListeners.splice(idx, 1)
    ;(socket as Socket & { off: (ev: string, fn: (...args: unknown[]) => void) => void } | null)?.off(
      event,
      handler as (...args: unknown[]) => void
    )
  }
}
